import React, { useState, useRef, useEffect } from 'react';
import { useBrandingColors } from '../../../hooks/useBrandingColors';
import type { VacancyFieldStatus } from './types';
import circleCheckIcon from '../../../assets/icons/circle-check.png';
import listIcon from '../../../assets/icons/list-icon.png';

interface VacancyOverviewProps {
  fields: VacancyFieldStatus[];
  onFieldClick?: (fieldKey: string) => void;
}

export function VacancyOverview({ fields, onFieldClick }: VacancyOverviewProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const colors = useBrandingColors();
  const filledCount = fields.filter(f => f.status === 'filled').length;
  const totalCount = fields.length;
  const contentRef = useRef<HTMLDivElement>(null);
  const [contentHeight, setContentHeight] = useState(0);

  useEffect(() => {
    if (contentRef.current) {
      setContentHeight(contentRef.current.scrollHeight);
    }
  }, [isExpanded, fields]);

  const handleFieldClick = (fieldKey: string) => {
    if (!onFieldClick) return;
    onFieldClick(fieldKey);
  };

  return (
    <div className="border border-[#e2e4e9] rounded-[12px] bg-white overflow-hidden">
      {/* Header - always visible */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between px-4 py-3 hover:bg-[#f9fafb] transition-colors"
      >
        <div className="flex items-center gap-2">
          <img src={listIcon} alt="" className="w-4 h-4" />
          <span className="text-[14px] font-medium text-[#0a0d14]">Обзор заполненных полей</span>
          <span className="text-[12px] text-[#868C98]">{filledCount}/{totalCount}</span>
        </div>
        <svg
          width="16" height="16" viewBox="0 0 16 16" fill="none"
          className={`transition-transform duration-200 ${isExpanded ? 'rotate-90' : ''}`}
        >
          <path d="M6 4L10 8L6 12" stroke="#525866" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>

      {/* Expanded content */}
      <div
        style={{
          maxHeight: isExpanded ? `${contentHeight}px` : '0px',
          overflow: 'hidden',
          transition: 'max-height 0.3s ease-in-out',
        }}
      >
        <div ref={contentRef} className="px-4 pb-4 flex flex-col gap-0">
          {fields.map((field) => (
            <div
              key={field.key}
              className={`flex items-start gap-2 px-2 py-2.5 rounded-[8px] transition-colors group ${
                onFieldClick ? 'cursor-pointer hover:bg-[#f6f8fa]' : ''
              }`}
              onClick={() => handleFieldClick(field.key)}
            >
              <span className="shrink-0 mt-0.5">
                {field.status === 'filled' ? (
                  <img src={circleCheckIcon} alt="✓" className="w-[18px] h-[18px]" />
                ) : (
                  <svg width="18" height="18" viewBox="0 0 20 20" fill="none">
                    <circle cx="10" cy="10" r="9" stroke="#CDD0D5" strokeWidth="1" />
                  </svg>
                )}
              </span>
              <div className="flex-1 min-w-0">
                <div className="text-[14px] font-medium text-[#0a0d14]">{field.label}</div>
                {field.details && field.details.length > 0 && (
                  <div className="mt-1 space-y-0.5">
                    {field.details.map((d, idx) => (
                      <div key={idx} className="text-[12px] text-[#868C98] leading-[1.4]">
                        {d.label ? <><span className="text-[#525866]">{d.label}:</span> {d.value || '—'}</> : (d.value || '—')}
                      </div>
                    ))}
                  </div>
                )}
              </div>
              {onFieldClick && (
                <svg
                  width="16" height="16" viewBox="0 0 16 16" fill="none"
                  className="shrink-0 self-center opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  <path d="M6 4L10 8L6 12" stroke="#868C98" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
